import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Pencil,
    X,
    BarChart3
} from 'lucide-react';
import { API } from '../../config';

const AdminStats = () => {
    const [stats, setStats] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingStat, setEditingStat] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        label: '',
        value: '',
        suffix: '',
        order: 0
    });

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to fetch stats');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (editingStat) {
                await axios.put(`${API}/stats/${editingStat.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Stat updated!');
            } else {
                await axios.post(`${API}/stats`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Stat added!');
            }
            resetForm();
            fetchStats();
        } catch (error) {
            toast.error('Failed to save stat');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setEditingStat(null);
        setFormData({ label: '', value: '', suffix: '', order: 0 });
    };

    const handleEdit = (stat) => {
        setEditingStat(stat);
        setFormData({
            label: stat.label,
            value: stat.value,
            suffix: stat.suffix || '',
            order: stat.order || 0
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this stat?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/stats/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Stat deleted!');
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete stat');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Homepage Stats</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage key performance indicators shown on the home page</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm flex items-center font-medium text-sm"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Stat
                </motion.button>
            </div>

            {/* Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {editingStat ? 'Edit Stat' : 'Add New Stat'}
                                </h3>
                                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Label (e.g. Farmers Partnered)</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.label}
                                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-gray-900 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Value (e.g. 25)</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.value}
                                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-gray-900 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Suffix (e.g. k+ or %)</label>
                                    <input
                                        type="text"
                                        value={formData.suffix}
                                        onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-gray-900 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-gray-900 outline-none"
                                    />
                                </div>
                                <div className="md:col-span-2 flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-8 py-2.5 rounded-xl font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Save Stat'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-8 py-2.5 rounded-xl border border-gray-300 font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat) => (
                    <div key={stat.id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center text-center relative group">
                        <div className="text-3xl font-bold text-gray-900">
                            {stat.value}{stat.suffix}
                        </div>
                        <div className="text-gray-500 text-sm mt-1">{stat.label}</div>

                        <div className="absolute top-2 right-2 flex opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEdit(stat)} className="p-1.5 text-gray-400 hover:text-gray-900">
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDelete(stat.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {stats.length === 0 && (
                    <div className="col-span-full py-12 text-center text-gray-400 flex flex-col items-center">
                        <BarChart3 className="h-12 w-12 mb-3 opacity-20" />
                        <p>No stats configured yet</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AdminStats;
